/**
 * Duration Calculation Service
 * Auto-calculates job duration using Google Maps API + dwell times
 * Features: Route duration, dwell time settings, learning from driver app data
 */

import { supabase } from '../supabaseClient';
import { calculateRoute, getRoutingConfig } from './routingEngineService';
import { dwellMinutesForAddress, invalidateDwellCache } from './dwellService';

/**
 * Calculate total job duration including drive time and dwell times
 * @param {Array} pickupStops - Array of pickup stop objects with addresses
 * @param {Array} deliveryStops - Array of delivery stop objects with addresses
 * @returns {Object} Duration calculation result
 */
export const calculateJobDuration = async (pickupStops, deliveryStops) => {
  const validPickups = pickupStops.filter(s => s.address?.trim());
  const validDeliveries = deliveryStops.filter(s => s.address?.trim());

  if (validPickups.length === 0 || validDeliveries.length === 0) {
    return { success: false, error: 'Need at least one pickup and one delivery address' };
  }

  try {
    // Build ordered stop list: all pickups first, then all deliveries
    const allStops = [
      ...validPickups.map((s, i) => ({ ...s, stopType: 'pickup', sequence: i + 1 })),
      ...validDeliveries.map((s, i) => ({ ...s, stopType: 'delivery', sequence: i + 1 }))
    ];

    // Get dwell times for each stop
    const stopsWithDwell = await Promise.all(
      allStops.map(async (stop) => {
        const dwellMinutes = await getDwellTimeForAddress(stop.address, stop.stopType);
        return { ...stop, dwellMinutes };
      })
    );

    // Calculate route duration using Google Maps (or simulation)
    const routeResult = await calculateRoute(stopsWithDwell);
    
    if (!routeResult.success) {
      return { success: false, error: 'Failed to calculate route' };
    }

    // Total drive time in minutes
    const driveTimeMinutes = Math.round(routeResult.totalDurationInTrafficSeconds / 60);
    
    // Total dwell time in minutes
    const totalDwellMinutes = stopsWithDwell.reduce((sum, stop) => sum + stop.dwellMinutes, 0);
    
    // Total job duration in hours
    const totalMinutes = driveTimeMinutes + totalDwellMinutes;
    const totalHours = Math.round(totalMinutes / 60 * 10) / 10; // Round to 1 decimal

    return {
      success: true,
      driveTimeMinutes,
      totalDwellMinutes,
      totalMinutes,
      totalHours,
      distanceKm: routeResult.totalDistanceKm,
      distanceMiles: routeResult.totalDistanceMiles,
      stops: stopsWithDwell,
      breakdown: {
        driveTime: formatDuration(driveTimeMinutes),
        dwellTime: formatDuration(totalDwellMinutes),
        totalTime: formatDuration(totalMinutes)
      },
      isSimulated: routeResult.isSimulated || false
    };
  } catch (error) {
    console.error('Duration calculation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get dwell time for a specific address
 * Checks cache first, then uses AI-adjusted or default values
 */
const getDwellTimeForAddress = async (address, stopType = 'customer') => {
  // Delegates to the one resolver (services/dwellService.js). The old body did an
  // ilike '%first 50 chars%' scan of dwell_time_settings, which never matched
  // anything (no per-location rows existed), then fell through to a type default
  // keyed off pickup-vs-delivery only.
  try {
    return await dwellMinutesForAddress(address, stopType === 'pickup' ? 'pickup' : 'delivery');
  } catch (error) {
    console.error('Error getting dwell time:', error);
    return getRoutingConfig().defaultDwellMinutes || 30;
  }
};

/**
 * Record actual dwell time from driver app for learning
 */
export const recordActualDwellTime = async ({
  loadId,
  driverId,
  stopId,
  locationAddress,
  arrivalTime,
  departureTime,
  expectedDwellMinutes
}) => {
  try {
    const arrival = new Date(arrivalTime);
    const departure = new Date(departureTime);
    const actualDwellMinutes = Math.round((departure - arrival) / 60000);
    const varianceMinutes = actualDwellMinutes - (expectedDwellMinutes || 30);

    // The dwell_time_history triggers (sql/dwell_learning.sql) resolve or create
    // the location's setting, stamp expected/variance and recompute the learned
    // value. This function only logs the sample.

    // Record the dwell time history
    const { data, error } = await supabase
      .from('dwell_time_history')
      .insert({
        load_id: loadId,
        driver_id: driverId,
        stop_id: stopId,
        location_address: locationAddress,
        arrival_time: arrivalTime,
        departure_time: departureTime,
        actual_dwell_minutes: actualDwellMinutes,
        expected_dwell_minutes: expectedDwellMinutes,
        variance_minutes: varianceMinutes,
        source: 'driver_app'
      })
      .select()
      .single();

    if (error) throw error;
    invalidateDwellCache(); // the trigger just moved the learned value

    return { success: true, data, actualDwellMinutes, varianceMinutes };
  } catch (error) {
    console.error('Error recording dwell time:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get dwell time settings for management UI
 */
export const getDwellTimeSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('dwell_time_settings')
      .select('*')
      .order('location_name');

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching dwell time settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update dwell time setting
 */
export const updateDwellTimeSetting = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('dwell_time_settings')
      .update({
        ...updates,
        last_updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating dwell time setting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create new dwell time setting
 */
export const createDwellTimeSetting = async (setting) => {
  try {
    const { data, error } = await supabase
      .from('dwell_time_settings')
      .insert(setting)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating dwell time setting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete dwell time setting
 */
export const deleteDwellTimeSetting = async (id) => {
  try {
    const { error } = await supabase
      .from('dwell_time_settings')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting dwell time setting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get dwell time history for analytics
 */
export const getDwellTimeHistory = async (filters = {}) => {
  try {
    let query = supabase
      .from('dwell_time_history')
      .select(`
        *,
        dwell_setting:dwell_time_settings(location_name, location_type),
        driver:drivers(id, phone)
      `)
      .order('recorded_at', { ascending: false });

    if (filters.dwellSettingId) {
      query = query.eq('dwell_setting_id', filters.dwellSettingId);
    }
    if (filters.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }
    if (filters.startDate) {
      query = query.gte('recorded_at', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('recorded_at', filters.endDate);
    }
    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching dwell time history:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cache route duration to reduce API calls
 */
export const cacheRouteDuration = async (origin, destination, routeData) => {
  try {
    const { data, error } = await supabase
      .from('route_duration_cache')
      .upsert({
        origin_address: origin.address,
        destination_address: destination.address,
        origin_lat: origin.lat,
        origin_lng: origin.lng,
        destination_lat: destination.lat,
        destination_lng: destination.lng,
        distance_meters: routeData.distanceMeters,
        duration_seconds: routeData.durationSeconds,
        duration_in_traffic_seconds: routeData.durationInTrafficSeconds,
        cached_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      }, { onConflict: 'origin_address,destination_address' })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error caching route duration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get cached route duration
 */
export const getCachedRouteDuration = async (originAddress, destinationAddress) => {
  try {
    const { data, error } = await supabase
      .from('route_duration_cache')
      .select('*')
      .eq('origin_address', originAddress)
      .eq('destination_address', destinationAddress)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    if (data) {
      // Increment hit count
      await supabase
        .from('route_duration_cache')
        .update({ hit_count: data.hit_count + 1 })
        .eq('id', data.id);
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error getting cached route duration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Format duration in minutes to human readable string
 */
const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

export default {
  calculateJobDuration,
  recordActualDwellTime,
  getDwellTimeSettings,
  updateDwellTimeSetting,
  createDwellTimeSetting,
  deleteDwellTimeSetting,
  getDwellTimeHistory,
  cacheRouteDuration,
  getCachedRouteDuration
};
